import type {
  Baseline,
  IElement,
  OneOfChildElements,
  Project,
} from '../models/project.model';
import type { IProposal, ActionId } from '../models/proposals.model';
import type { Action, ProjectChange } from './pipeline/update-engine.models';
import { ProjectChangeType } from './pipeline/update-engine.models';
import { createAction } from './actions/action.factory';
import { createProposal } from './proposal-factory.helpers';
import {
  flattenElements,
  getAllBuildingVersions,
  getElementById,
  isElement,
} from './recursive-element.helpers';
import { getElementName } from './element.helpers';
import {
  applyVersionMetadata,
  hasChanges,
  replaceBuildingVersionInProject,
} from './project.helpers';
import { omitEmbeddedRecipesFromProject } from './omit-embedded-recipes.helpers';
import {
  applyProjectChanges,
  applyProjectChangesToParent,
} from './pipeline/update-engine.helpers';
import {
  findNextPreferredProposalColorIndex,
  normalizeProposalsColorIndices,
  updateElementActiveVersionStates,
} from './proposal.helpers';
import { PROPOSAL_COLORS } from './proposals/proposal-colors.constants';
import { omit } from './object.helpers';
import { guards } from './typeguards.helpers';
import { sanitizeProposalActionChangesInVersion } from './actions/sanitize-proposal-action-changes.helpers';
import { normalizeLegacyProjectShape } from './project-shape-migration.helpers';
import { ensureVersionHasProposal } from './proposal-factory.helpers';

/** Legacy palette slot 0 (grey) before assigning a free concrete palette index */
const MIGRATION_LEGACY_GREY_COLOR_SENTINEL = -1;

/**
 * Maps persisted {@link IProposal.colorIndex} from before DEV-3070 onto current palette slots.
 *
 * Legacy encoding used grey at index 0 and {@link PROPOSAL_COLORS} at indices 1..n; the aligned model
 * uses only 0..n-1 on {@link PROPOSAL_COLORS}. Legacy grey is tagged with {@link MIGRATION_LEGACY_GREY_COLOR_SENTINEL}.
 */
const remapLegacyPersistedProposalPaletteSlots = (
  proposals: IProposal[],
): IProposal[] => {
  const legacyLen = PROPOSAL_COLORS.length + 1;
  return proposals.map((p) => {
    const legacySlot = ((p.colorIndex % legacyLen) + legacyLen) % legacyLen;
    if (legacySlot === 0) {
      return { ...p, colorIndex: MIGRATION_LEGACY_GREY_COLOR_SENTINEL };
    }
    return { ...p, colorIndex: legacySlot - 1 };
  });
};

/**
 * Assigns palette indices to legacy-grey rows so they do not collide with explicit remapped slots.
 *
 * @param proposals - Rows already processed by {@link remapLegacyPersistedProposalPaletteSlots}
 */
const assignSlotsForMigrationLegacyGreyProposals = (
  proposals: IProposal[],
): IProposal[] => {
  if (
    !proposals.some(
      (p) => p.colorIndex === MIGRATION_LEGACY_GREY_COLOR_SENTINEL,
    )
  ) {
    return proposals;
  }
  const used = new Set<number>();
  for (const p of proposals) {
    if (p.colorIndex !== MIGRATION_LEGACY_GREY_COLOR_SENTINEL) {
      used.add(p.colorIndex);
    }
  }
  return proposals.map((p) => {
    if (p.colorIndex !== MIGRATION_LEGACY_GREY_COLOR_SENTINEL) return p;
    const idx = findNextPreferredProposalColorIndex(used);
    used.add(idx);
    return { ...p, colorIndex: idx };
  });
};

/**
 * Reindexes proposal rows still using the pre–DEV-3070 palette encoding (e.g. proposalsV2 on disk).
 */
const rebaseLegacyPersistedProposalPaletteIndices = (
  proposals: IProposal[],
): IProposal[] =>
  assignSlotsForMigrationLegacyGreyProposals(
    remapLegacyPersistedProposalPaletteSlots(proposals),
  );

/**
 * Legacy proposal migration: persisted projects may still use v1 proposal rows
 * (`selections`, `active`) and version groups keyed by element `versionId`. This
 * module converts them to the current shape (`IProposal.actionIds` → `Action`
 * rows, baseline selections applied to the element tree) at read time and in the
 * compute pipeline so the rest of the app only sees action-backed proposals.
 *
 * TODO: Remove read-time migration entry points (client `fetchProject` /
 * `fetchPublicProject`, pipeline version migration) once every production project
 * has been backfilled and saved without legacy fields; keep server migrations /
 * admin repair tools as needed until then.
 */
type LegacyBuildingVersion = Baseline & {
  deprecatedProposals?: LegacyProposal[];
};

/** Persisted rollout field: canonical action-backed rows; `proposals` must still be an array when set. */
type BuildingVersionWithProposalsV2 = LegacyBuildingVersion & {
  proposalsV2?: (IProposal | LegacyProposal)[] | null;
};

type LegacyProposal = Omit<IProposal, 'actionIds'> & {
  actionIds?: ActionId[];
  active?: boolean;
  selections?: Record<string, IElement['id'] | undefined>;
};

type LegacyElementVersionFields = IElement & {
  versionId?: string;
  elements: LegacyChildElementVersionFields[];
};

type LegacyChildElementVersionFields = OneOfChildElements & {
  versionId?: string;
  elements?: LegacyChildElementVersionFields[];
};

/** True when the row is already the persisted action-proposal shape (no v1 selections/active). */
const isActionProposal = (
  proposal: LegacyProposal | IProposal,
): proposal is IProposal =>
  !('selections' in proposal) && !('active' in proposal);

const toActionProposal = (proposal: IProposal | LegacyProposal): IProposal => {
  const base =
    'selections' in proposal || 'active' in proposal
      ? (({ selections: _selections, active: _active, ...rest }) => rest)(
          proposal as LegacyProposal,
        )
      : proposal;
  return {
    ...base,
    actionIds: base.actionIds ?? [],
  };
};

const getLegacyElementVersionId = (element: IElement): string | undefined => {
  const legacyVersionIdKey = 'versionId';
  return (element as LegacyElementVersionFields)[legacyVersionIdKey];
};

const removeLegacyElementVersionFieldsFromElements = (
  elements: OneOfChildElements[],
): OneOfChildElements[] =>
  elements.map((element) => {
    if (!isElement(element)) return element;
    const {
      versionId: _versionId,
      isActiveVersion: _isActiveVersion,
      ...rest
    } = element as LegacyElementVersionFields;
    return {
      ...rest,
      elements: removeLegacyElementVersionFieldsFromElements(rest.elements),
    };
  });

export const removeLegacyElementVersionFields = (
  version: Baseline,
): Baseline => ({
  ...version,
  elements: removeLegacyElementVersionFieldsFromElements(version.elements),
});

const removeDeprecatedProposals = (
  version: Baseline | LegacyBuildingVersion,
): Baseline => {
  const { deprecatedProposals: _deprecatedProposals, ...cleanVersion } =
    version as LegacyBuildingVersion;
  return cleanVersion;
};

const toVersionWithoutProposals = (
  version: LegacyBuildingVersion,
  proposals: (IProposal | LegacyProposal)[],
): Baseline => {
  const { deprecatedProposals: _deprecatedProposals, ...rest } = version;
  return {
    ...rest,
    proposals: proposals.map(toActionProposal),
    resultsRecord: version.resultsRecord ?? {},
  };
};

/**
 * Drops {@link Baseline.actions} not listed on any proposal so migrated JSON
 * satisfies {@link isValidProposalsAndActions} (no orphan actions).
 *
 * Also removes rollout-only fields `proposalsV2` and `deprecatedProposals` so persisted
 * versions never keep stale duplicate proposal sources after migration.
 */
const pruneVersionActionsToProposals = (
  version: Baseline,
  proposals: IProposal[],
): Baseline => {
  const referenced = new Set(proposals.flatMap((p) => p.actionIds));
  const pruned = {
    ...version,
    actions: (version.actions ?? []).filter((a) => referenced.has(a.id)),
  };
  return omit(
    pruned as LegacyBuildingVersion & BuildingVersionWithProposalsV2,
    'proposalsV2',
    'deprecatedProposals',
  ) as Baseline;
};

/**
 * Builds a lookup of `versionId -> elementId[]` from all {@link IElement} nodes in a version.
 * Elements sharing the same `versionId` are siblings in a version group.
 */
export const buildVersionGroupMap = (
  version: Baseline,
): Map<string, string[]> => {
  const map = new Map<string, string[]>();
  const allElements = flattenElements(version).filter(isElement);

  for (const element of allElements) {
    const versionId = getLegacyElementVersionId(element);
    if (!versionId) continue;

    const group = map.get(versionId);
    if (group) {
      group.push(element.id);
    } else {
      map.set(versionId, [element.id]);
    }
  }

  return map;
};

/**
 * Computes the diff between a non-baseline proposal and the baseline proposal's selections.
 *
 * For each version group where the two proposals disagree:
 *  - Emit {@link ProjectChangeType.ElementSoftDelete} for the baseline-selected element
 *  - Emit {@link ProjectChangeType.ElementActivate} for the proposal-selected element
 *
 * Returns an empty array when proposals have identical selections (or no version groups exist).
 *
 * @param proposalSelections - Selections of the proposal being migrated (non-baseline)
 * @param baselineSelections - Selections of the baseline (first legacy) proposal
 * @param versionGroupMap - Precomputed `versionId -> elementId[]` lookup
 */
export const selectionsDiffToChanges = (
  proposalSelections: NonNullable<LegacyProposal['selections']> = {},
  baselineSelections: NonNullable<LegacyProposal['selections']> = {},
  versionGroupMap: Map<string, string[]>,
): ProjectChange[] => {
  const changes: ProjectChange[] = [];

  // Collect all version groups referenced by either proposal
  const allVersionIds = new Set([
    ...Object.keys(proposalSelections),
    ...Object.keys(baselineSelections),
  ]);

  for (const versionId of allVersionIds) {
    const siblings = versionGroupMap.get(versionId);
    if (!siblings) continue;

    const baselineElementId = baselineSelections[versionId];
    const proposalElementId = proposalSelections[versionId];

    // Same selection (or both absent) → no diff
    if (baselineElementId === proposalElementId) continue;

    // Delete what baseline has active but this proposal does not want
    if (baselineElementId && baselineElementId !== proposalElementId) {
      changes.push({
        type: ProjectChangeType.ElementSoftDelete,
        elementId: baselineElementId,
      });
    }

    // Activate what this proposal wants that baseline had deactivated
    if (proposalElementId && proposalElementId !== baselineElementId) {
      changes.push({
        type: ProjectChangeType.ElementActivate,
        elementId: proposalElementId,
      });
    }
  }

  return changes;
};

/**
 * Builds a human-readable name describing what a migrated action does, by listing
 * activated, deactivated and deleted element names resolved against the version.
 *
 * Format: `Activate <names> · Deactivate <names> · Delete <names>`. Either segment is omitted when
 * its list is empty. Returns `undefined` when there are no activate/deactivate/delete
 * changes (caller should leave the action unnamed in that case).
 *
 * @param changes - Diff produced by {@link selectionsDiffToChanges}
 * @param version - Version used to resolve element names via {@link getElementName}
 */
export const buildMigrationActionName = (
  changes: ProjectChange[],
  version: Baseline,
): string | undefined => {
  const activatedNames: string[] = [];
  const deactivatedNames: string[] = [];
  const deletedNames: string[] = [];

  for (const change of changes) {
    if (
      change.type !== ProjectChangeType.ElementActivate &&
      change.type !== ProjectChangeType.ElementDeactivate &&
      change.type !== ProjectChangeType.ElementSoftDelete
    ) {
      continue;
    }
    const element = getElementById(version, change.elementId);
    const name = getElementName(element, version.products);
    if (change.type === ProjectChangeType.ElementActivate) {
      activatedNames.push(name);
    } else if (change.type === ProjectChangeType.ElementDeactivate) {
      deactivatedNames.push(name);
    } else {
      deletedNames.push(name);
    }
  }

  const segments: string[] = [];
  if (activatedNames.length) {
    segments.push(`Activate ${activatedNames.join(', ')}`);
  }
  if (deactivatedNames.length) {
    segments.push(`Deactivate ${deactivatedNames.join(', ')}`);
  }
  if (deletedNames.length) {
    segments.push(`Delete ${deletedNames.join(', ')}`);
  }

  return segments.length ? segments.join(' · ') : undefined;
};

/**
 * Migrate a single non-baseline legacy {@link IProposal} to an {@link IProposal} plus one
 * backing {@link Action} per {@link ProjectChange}. Changes are computed as a diff
 * against the baseline (active) proposal's selections.
 *
 * Each action contains exactly one change so users can toggle individual
 * activate/delete operations independently from the proposal's action list.
 *
 * Callers should ensure element `isActiveVersion` flags match that baseline (e.g. run
 * {@link migrateProposalsInVersion}, which applies {@link updateElementActiveVersionStates} first).
 *
 * @param proposal - The non-baseline v1 proposal to migrate
 * @param baselineSelections - Selections from the baseline (first legacy) proposal
 * @param versionGroupMap - Precomputed `versionId -> elementId[]` lookup
 * @param version - The building version, used to resolve element names for action labels
 */
export const migrateLegacyProposal = (
  legacyProposal: LegacyProposal,
  baselineSelections: NonNullable<LegacyProposal['selections']>,
  versionGroupMap: Map<string, string[]>,
  version: Baseline,
): { proposal: IProposal; actions: Action[] } => {
  const changes = selectionsDiffToChanges(
    legacyProposal.selections,
    baselineSelections,
    versionGroupMap,
  );

  // One action per change so each activate/delete is independently toggleable
  const actions = changes.map((change) =>
    createAction({
      changes: [change],
      name: buildMigrationActionName([change], version),
    }),
  );

  const proposal = createProposal({
    name: legacyProposal.name,
    colorIndex: legacyProposal.colorIndex,
    actionIds: actions.map((a) => a.id),
    resultsRecord: legacyProposal.resultsRecord,
  });

  return { proposal, actions };
};

/**
 * Applies the baseline proposal's selections to the element tree by setting
 * {@link IElement.isDeactivated} on version-group siblings not selected by the baseline.
 *
 * This "bakes" the baseline proposal into the tree so element visibility no longer
 * depends on proposal context — non-selected siblings are deactivated directly.
 */
export const applyBaselineSelectionsToElements = (
  version: LegacyBuildingVersion,
  baselineSelections: NonNullable<LegacyProposal['selections']>,
  versionGroupMap: Map<string, string[]>,
): LegacyBuildingVersion => {
  const changes: ProjectChange[] = [];

  for (const [versionId, siblings] of versionGroupMap) {
    const selectedElementId = baselineSelections?.[versionId];

    for (const siblingId of siblings) {
      // Non-selected siblings become deactivated on the tree
      changes.push({
        type:
          siblingId === selectedElementId
            ? ProjectChangeType.ElementActivate
            : ProjectChangeType.ElementDeactivate,
        elementId: siblingId,
      });
    }
  }

  if (!changes.length) return version;
  return applyProjectChangesToParent(version, changes) as LegacyBuildingVersion;
};

/**
 * Read-time migration: populates {@link Baseline.proposals} and {@link Baseline.actions}
 * from existing {@link IProposal} entries when `proposals` is absent or empty.
 *
 * First applies legacy proposal metadata via {@link updateElementActiveVersionStates} so
 * `isActiveVersion` matches baseline selections where that helper is non-trivial (legacy data may
 * have been out of sync). Then "bakes" the baseline into the tree by setting
 * `isDeactivated = true` on version-group siblings not selected by the baseline proposal via
 * {@link applyBaselineSelectionsToElements}.
 *
 * The **first** legacy v1 row in persisted {@link Baseline.proposals} order is the baseline
 * (display "Proposal 1"); it is **excluded** from the migrated `proposals` array. Remaining legacy
 * rows are migrated; their changes are computed as a diff against that baseline's selections.
 *
 * The baseline row's {@link IProposal.resultsRecord} is merged onto {@link Baseline.resultsRecord}
 * (proposal keys win) so baseline totals and {@link getResultsRecord} stay correct after v1 proposals are retired from the tree.
 *
 * When persisted `proposalsV2` is set (non-null), overwrites
 * `proposals` from that array, keeps only `actions` referenced by those rows, and drops `proposalsV2`.
 * {@link IProposal.colorIndex} values on those rows are interpreted as the pre–DEV-3070 palette (grey at 0,
 * bar colours at 1..n) and rebased onto current {@link PROPOSAL_COLORS} indices (same as `deprecatedProposals`
 * and the merged output of the v1 → action migration).
 * Requires `actions` to be an array on the version (throws otherwise).
 *
 * When `proposalsV2` is not an array (field absent or rollout placeholder), `actions` is already an
 * array, and every {@link IProposal} row is action-backed, the version is treated as already
 * migrated on disk: only normalization / prune / rollout-field stripping run (no v1 replay).
 *
 * Versions that would otherwise have no proposals receive one empty proposal.
 */
const migrateProposalsInVersionInternal = (
  version: LegacyBuildingVersion,
): Baseline => {
  if (version.deprecatedProposals) {
    const nextProposals = rebaseLegacyPersistedProposalPaletteIndices(
      version.deprecatedProposals.map(toActionProposal),
    );
    return pruneVersionActionsToProposals(
      toVersionWithoutProposals(version, nextProposals),
      nextProposals,
    );
  }

  const versionWithV2 = version as BuildingVersionWithProposalsV2;
  const proposalsV2 = versionWithV2.proposalsV2;

  const persistedProposals = (
    version as { proposals?: (IProposal | LegacyProposal)[] }
  ).proposals;

  // Canonical JSON already: no proposalsV2 array to merge, actions persisted, proposals v2-shaped
  if (
    !guards.is.array(proposalsV2) &&
    guards.is.array(version.actions) &&
    (persistedProposals ?? []).every(isActionProposal)
  ) {
    const nextProposals = normalizeProposalsColorIndices(
      (persistedProposals ?? []).map(toActionProposal),
    );
    return pruneVersionActionsToProposals(
      toVersionWithoutProposals(version, nextProposals),
      nextProposals,
    );
  }

  // Project has already been migrated to new proposal model with action-backed proposals
  if (guards.is.array(proposalsV2)) {
    if (!guards.is.array(version.actions)) {
      throw new Error(
        `[migrateProposalsInVersion] proposalsV2 is set but actions is missing or not an array (version ${version.id}).`,
      );
    }

    // Remove v2 and deprecated proposals from the version
    const rest = omit(versionWithV2, 'proposalsV2', 'deprecatedProposals');

    // Reindex palette slots (legacy grey at index 0 → aligned PROPOSAL_COLORS indices)
    const nextProposals = rebaseLegacyPersistedProposalPaletteIndices(
      proposalsV2.map(toActionProposal),
    );
    return pruneVersionActionsToProposals(
      {
        ...rest,
        proposals: nextProposals,
        actions: version.actions,
        resultsRecord: version.resultsRecord ?? {},
      },
      nextProposals,
    );
  }

  const proposals = (version as { proposals?: (IProposal | LegacyProposal)[] })
    .proposals;
  if (!proposals?.length) {
    return pruneVersionActionsToProposals(
      toVersionWithoutProposals(version, []),
      [],
    );
  }
  if (proposals.every(isActionProposal)) {
    const nextProposals = normalizeProposalsColorIndices(proposals);
    return pruneVersionActionsToProposals(
      toVersionWithoutProposals(version, nextProposals),
      nextProposals,
    );
  }

  const legacyProposals = proposals.filter(
    (proposal): proposal is LegacyProposal => !isActionProposal(proposal),
  );
  const persistedActionProposals = proposals.filter(isActionProposal);

  // Baseline follows the first legacy row in persisted list order (UI "Proposal 1"), not `active`
  const baselineLegacyProposal = legacyProposals[0];

  // Align version-group flags from the legacy proposal list before computing diffs
  let workingVersion = updateElementActiveVersionStates({
    ...version,
    proposals: legacyProposals.map((proposal) => ({
      ...proposal,
      actionIds: proposal.actionIds ?? [],
    })),
  });

  const baselineSelections: NonNullable<LegacyProposal['selections']> =
    baselineLegacyProposal?.selections ?? {};

  const versionGroupMap = buildVersionGroupMap(workingVersion);

  // Bake baseline into the tree: deactivate non-selected siblings
  workingVersion = applyBaselineSelectionsToElements(
    workingVersion,
    baselineSelections,
    versionGroupMap,
  );

  const migratedActions: Action[] = [];
  const migratedProposals: IProposal[] = [];

  for (const legacyProposal of legacyProposals) {
    // Baseline row is merged into the tree — omit from persisted proposal list
    if (legacyProposal === baselineLegacyProposal) continue;

    const { proposal, actions } = migrateLegacyProposal(
      legacyProposal,
      baselineSelections,
      versionGroupMap,
      workingVersion,
    );

    migratedActions.push(...actions);
    migratedProposals.push(proposal);
  }

  const mergedProposals = rebaseLegacyPersistedProposalPaletteIndices([
    ...persistedActionProposals.map(toActionProposal),
    ...migratedProposals,
  ]);
  const mergedActions = [...(workingVersion.actions ?? []), ...migratedActions];

  // Use results record from baseline proposal if it exists, otherwise use the version's results record
  const resultsRecord = baselineLegacyProposal
    ? baselineLegacyProposal.resultsRecord
    : (workingVersion.resultsRecord ?? {});

  return pruneVersionActionsToProposals(
    {
      ...workingVersion,
      actions: mergedActions,
      proposals: mergedProposals,
      resultsRecord,
    },
    mergedProposals,
  );
};

/**
 * Migrates legacy proposal data and enforces the current minimum-one-proposal
 * invariant. The fallback proposal has no actions and is created only when the
 * migrated version would otherwise be empty.
 */
export const migrateProposalsInVersion = (
  version: LegacyBuildingVersion,
): Baseline =>
  ensureVersionHasProposal(migrateProposalsInVersionInternal(version));

/**
 * Migrate all building versions in a project that lack `proposals`.
 * When any version is updated, {@link applyVersionMetadata} runs so slim {@link Project.results}
 * is taken from {@link Baseline.resultsRecord}[active version id] (same rule as enrich).
 * Returns the same project reference when no migration was needed.
 */
export const migrateProposalsInProject = (project: Project): Project => {
  // Normalize legacy building-layer documents (old exports / stored rows) into
  // the flattened DEV-2576 shape before migrating proposals (idempotent).
  project = normalizeLegacyProjectShape(project);
  let replacedAnyVersion = false;

  for (const version of getAllBuildingVersions(project)) {
    const migrated = removeLegacyElementVersionFields(
      removeDeprecatedProposals(
        sanitizeProposalActionChangesInVersion(
          migrateProposalsInVersion(version),
        ),
      ),
    );

    const migrationPatch: Pick<
      Baseline,
      'id' | 'elements' | 'actions' | 'proposals' | 'resultsRecord'
    > = {
      id: migrated.id,
      elements: migrated.elements,
      actions: migrated.actions,
      proposals: migrated.proposals,
      resultsRecord: migrated.resultsRecord,
    };

    // Partial merge via updateBuildingVersionProperties keeps unknown keys (e.g. proposalsV2).
    const hasRolloutProposalFields =
      'proposalsV2' in version || 'deprecatedProposals' in version;

    if (!hasRolloutProposalFields && !hasChanges(version, migrationPatch)) {
      continue;
    }

    project = replaceBuildingVersionInProject(project, migrated);
    replacedAnyVersion = true;
  }

  if (replacedAnyVersion) {
    project = applyVersionMetadata(project);
  }

  return omitEmbeddedRecipesFromProject(project);
};

/**
 * Collects all element IDs referenced by {@link ProjectChangeType.ElementActivate} changes
 * across all actions linked to any {@link IProposal}. These are elements that were
 * deactivated during baseline baking and would be re-activated by a proposal.
 */
const collectActivatedElementIds = (version: Baseline): Set<string> => {
  const activatedIds = new Set<string>();

  const actionIdSet = new Set(
    (version.proposals ?? []).flatMap((p) => p.actionIds ?? []),
  );

  for (const action of version.actions ?? []) {
    if (!actionIdSet.has(action.id)) continue;

    for (const change of action.changes) {
      if (change.type === ProjectChangeType.ElementActivate) {
        activatedIds.add(change.elementId);
      }
    }
  }

  return activatedIds;
};

/**
 * Reverses the baseline baking done by {@link applyBaselineSelectionsToElements}.
 * Any element that a proposal would activate (i.e. has an {@link ProjectChangeType.ElementActivate}
 * change in any proposal action) gets `isDeactivated` set back to `false`.
 */
export const restoreDeactivatedFromProposals = (
  version: Baseline,
): Baseline => {
  const activatedIds = collectActivatedElementIds(version);
  if (!activatedIds.size) return version;

  const changes: ProjectChange[] = [...activatedIds].map((elementId) => ({
    type: ProjectChangeType.ElementActivate,
    elementId,
  }));

  return applyProjectChangesToParent(version, changes);
};

/**
 * Strip migrated proposal actions from all building versions and retain one
 * empty proposal per version.
 * Restores `isDeactivated` for elements that any proposal would have activated,
 * undoing the baseline baking from {@link applyBaselineSelectionsToElements},
 * via {@link applyProjectChanges} ({@link ProjectChangeType.ElementActivate} +
 * {@link ProjectChangeType.VersionClearProposals}).
 * Used by admin tooling to reset proposal state without violating validation.
 */
export const clearProposalsInProject = (project: Project): Project => {
  for (const version of getAllBuildingVersions(project)) {
    if (!version.proposals?.length && !version.actions?.length) continue;

    const activatedIds = collectActivatedElementIds(version);
    const changes: ProjectChange[] = [
      ...[...activatedIds].map((elementId): ProjectChange => ({
        type: ProjectChangeType.ElementActivate,
        elementId,
      })),
      {
        type: ProjectChangeType.VersionClearProposals,
        versionId: version.id,
      },
    ];

    project = applyProjectChanges(project, changes);
  }
  return project;
};
